/**
 * Typed fetch wrapper.
 *
 * - Attaches the Firebase ID token as `Authorization: Bearer <token>` on
 *   authed calls (public endpoints skip it).
 * - Parses the uniform error envelope `{ error, code, requestId, details? }`
 *   into a typed `ApiError` so callers can branch on `code` / status.
 * - On a 401 with code `invalid-token`, force-refresh the ID token once and
 *   retry. A 401 with code `unauthenticated` means no/expired session — no
 *   retry; the error propagates so RequireAuth can redirect to /login.
 */
import { getIdToken } from './firebase';
import type {
  ApiErrorBody,
  AppConfig,
  CreateQuestionInput,
  LeaderboardRow,
  PublicLeaderboard,
  Question,
  StudentProfile,
  Submission,
  OverrideInput,
  WeekRef,
} from './types';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string;
  readonly details?: unknown;

  constructor(status: number, body: Partial<ApiErrorBody>) {
    super(body.error || `Request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code || 'unknown';
    this.requestId = body.requestId || '';
    this.details = body.details;
  }

  /** Caller is signed in but not on the teacher allowlist. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  auth?: boolean; // attach bearer token (default true)
  signal?: AbortSignal;
}

async function parseError(res: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> = {};
  try {
    body = (await res.json()) as Partial<ApiErrorBody>;
  } catch {
    body = { error: res.statusText };
  }
  return new ApiError(res.status, body);
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true, signal } = opts;

  const send = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    const isForm = body instanceof FormData;
    if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(`/api${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
      signal,
    });
  };

  let token = auth ? await getIdToken(false) : null;
  let res = await send(token);

  // A 401 with `invalid-token` means our Bearer was valid-format but stale per the
  // server (signature/expiry/clock skew); a single force-refresh + retry handles
  // that race. A 401 with `unauthenticated` means no Bearer was attached or it
  // could not be parsed — refreshing would not help, so let the error propagate
  // and the auth flow redirect to /login.
  if (res.status === 401 && auth) {
    const peek = await parseError(res.clone());
    if (peek.code === 'invalid-token') {
      token = await getIdToken(true);
      if (token) res = await send(token);
    }
  }

  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/* -------------------------------------------------------------------------- */
/* Panel (authed)                                                             */
/* -------------------------------------------------------------------------- */

export const api = {
  getConfig: () => request<AppConfig>('/config'),
  updateTeacherEmails: (teacherEmails: string[]) =>
    request<AppConfig>('/config', { method: 'PUT', body: { teacherEmails } }),

  listQuestions: () => request<Question[]>('/questions'),
  /**
   * Like `listQuestions` but also surfaces the count of legacy (pre-set-refactor)
   * docs that the server silently filtered from the list, via the
   * `X-Hidden-Legacy-Count` response header. Used to render a small notice
   * explaining why totals may not match raw Firestore counts.
   */
  listQuestionsWithMeta: async (): Promise<{ rows: Question[]; hiddenLegacy: number }> => {
    const token = await getIdToken(false);
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    let res = await fetch('/api/questions', { headers });
    if (res.status === 401) {
      const peek = await parseError(res.clone());
      if (peek.code === 'invalid-token') {
        const fresh = await getIdToken(true);
        if (fresh) {
          headers.Authorization = `Bearer ${fresh}`;
          res = await fetch('/api/questions', { headers });
        }
      }
    }
    if (!res.ok) throw await parseError(res);

    // Header lookup is case-insensitive per Fetch spec; lowercase is conventional.
    const raw = res.headers.get('x-hidden-legacy-count');
    const parsed = raw === null ? 0 : Number.parseInt(raw, 10);
    const hiddenLegacy = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    const rows = (await res.json()) as Question[];
    return { rows, hiddenLegacy };
  },
  getQuestion: (id: string) => request<Question>(`/questions/${encodeURIComponent(id)}`),
  createQuestion: (input: CreateQuestionInput) =>
    request<Question>('/questions', { method: 'POST', body: input }),
  updateQuestion: (id: string, input: CreateQuestionInput) =>
    request<Question>(`/questions/${encodeURIComponent(id)}`, { method: 'PUT', body: input }),
  bulkCreateQuestions: (rows: CreateQuestionInput[]) =>
    request<{
      results: { index: number; ok: boolean; questionId?: string; error?: string }[];
      created: number;
      failed: number;
    }>('/questions/bulk', { method: 'POST', body: { rows } }),
  uploadQuestionImage: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<{ path: string }>('/uploads/question-image', {
      method: 'POST',
      body: form,
    });
  },

  listSubmissions: (
    filters: {
      weekKey?: string;
      questionId?: string;
      userId?: number;
      status?: string;
    } = {},
  ) => {
    const params = new URLSearchParams();
    if (filters.weekKey) params.set('weekKey', filters.weekKey);
    if (filters.questionId) params.set('questionId', filters.questionId);
    if (filters.userId !== undefined) params.set('userId', String(filters.userId));
    if (filters.status) params.set('status', filters.status);
    const qs = params.toString();
    return request<Submission[]>(`/submissions${qs ? `?${qs}` : ''}`);
  },
  getSubmission: (id: string) => request<Submission>(`/submissions/${encodeURIComponent(id)}`),
  overrideSubmission: (id: string, input: OverrideInput) =>
    request<Submission>(`/submissions/${encodeURIComponent(id)}/override`, {
      method: 'POST',
      body: input,
    }),

  getStudent: (userId: number) =>
    request<StudentProfile>(`/students/${encodeURIComponent(String(userId))}`),

  getLeaderboard: (weekKey?: string) =>
    request<LeaderboardRow[]>(
      `/leaderboard${weekKey ? `?weekKey=${encodeURIComponent(weekKey)}` : ''}`,
    ),
  listWeeks: () => request<WeekRef[]>('/weeks'),

  /* ---- Public (no auth) ---- */
  publicLeaderboard: (weekKey?: string) =>
    request<PublicLeaderboard>(
      `/public/leaderboard${weekKey ? `?weekKey=${encodeURIComponent(weekKey)}` : ''}`,
      { auth: false },
    ),
  publicWeeks: () => request<WeekRef[]>('/public/weeks', { auth: false }),

  /* ---- Super-admin (env-driven allowlist; 403 for non-supers) ---- */
  adminSnapshot: () => request<AdminSnapshot>('/admin/snapshot'),
  adminUpdateGrading: (patch: {
    autoGradeEnabled?: boolean;
    geminiModelOverride?: string | null;
    geminiMaxOutputTokensOverride?: number | null;
  }) =>
    request<AppConfig & { superAdminEmails: string[] }>('/admin/grading', {
      method: 'PUT',
      body: patch,
    }),
  adminUpdateRuntime: (patch: { submissionsEnabled?: boolean; tickEnabled?: boolean }) =>
    request<AppConfig & { superAdminEmails: string[] }>('/admin/runtime', {
      method: 'PUT',
      body: patch,
    }),
  adminRegradeSubmission: (id: string) =>
    request<{ ok: true; id: string }>(`/admin/submissions/${encodeURIComponent(id)}/regrade`, {
      method: 'POST',
    }),
};

export interface AdminSnapshot {
  config: AppConfig;
  superAdminEmails: string[];
  ownerEmails: string[];
  adminEmails: string[];
  /** True when the currently-signed-in viewer is on the owner allowlist;
   *  used by the UI to hide destructive controls from faculty admins. */
  viewerIsOwner: boolean;
  envFallbacks: {
    GEMINI_MODEL: string;
    GEMINI_MAX_OUTPUT_TOKENS: number;
    VERTEX_LOCATION: string;
    GCP_PROJECT: string;
  };
  knownModels: { id: string; label: string }[];
}
